import React, { useState, useEffect } from "react";
import { Card, Switch, Row, Col, DatePicker, Select } from "antd";
import dayjs from "dayjs";
import axiosInstance from "../axios.js";
import {
  COLOR_OPTIONS,
  DATE_RANGE_MAP,
  STATUS_DISPLAY_NAME,
  STUDENT_STATUS,
  TUTOR_STATUS,
  LEAD_TYPE,
  STATUS_ICONS,
} from "./constants";
import "./FlashCards.css";
import { useAtom } from "jotai";
import { citysList } from "../states/citys";
import {
  userRole,
  userTutorCityAccess,
  userStudentCityAccess,
} from "../states/userRole";
import { USER_ROLES } from "../constants/roleConstants.js";
import { Link } from "react-router-dom";
import OverviewCard from "./OverviewCard.js";
import { set } from "lodash";

const { Option } = Select;
const { RangePicker } = DatePicker;

const FlashCards = () => {
  const [loading, setLoading] = useState(false);
  const [isTutors, setIsTutors] = useState(false);
  const [isCreatedAt, setIsCreatedAt] = useState(false);
  const [flashcards, setFlashcards] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [presetRange, setPresetRange] = useState("today");
  const [dateRangeType, setDateRangeType] = useState("relative");
  const [error, setError] = useState(null);
  const [cities] = useAtom(citysList);
  const [role] = useAtom(userRole);
  const [tutorCityAccess] = useAtom(userTutorCityAccess);
  const [studentCityAccess] = useAtom(userStudentCityAccess);
  const [subscribeCount, setSubscribeCount] = useState(0);
  const [expiryCount, setExpiryCount] = useState(0);

  const defaultStudentCities =
    role !== USER_ROLES.SUPERADMIN
      ? studentCityAccess?.map((city_id) => city_id)
      : Object.keys(cities).map((city_id) => city_id);
  const defaultTutorCities =
    role !== USER_ROLES.SUPERADMIN
      ? tutorCityAccess?.map((city_id) => city_id)
      : Object.keys(cities).map((city_id) => city_id);
  const [selectedCities, setSelectedCities] = useState(
    isTutors ? defaultTutorCities : defaultStudentCities
  );

  useEffect(() => {
    // console.clear();
    // console.log({
    //   isTutors,
    //   dateRange,
    //   presetRange,
    //   dateRangeType,
    //   selectedCities,
    // });

    if (dateRangeType !== "relative" && dateRange.length === 0) return;
    if (dateRange) fetchFlashcards();
  }, [
    isTutors,
    dateRange,
    presetRange,
    dateRangeType,
    selectedCities,
    isCreatedAt,
  ]);

  const fetchFlashcards = async () => {
    try {
      setLoading(true);
      let fromDate = "",
        toDate = "";

      if (
        dateRangeType === "relative" &&
        presetRange &&
        DATE_RANGE_MAP[presetRange]
      ) {
        fromDate = DATE_RANGE_MAP[presetRange].fromDate;
        toDate = DATE_RANGE_MAP[presetRange].toDate;
      } else if (dateRangeType === "absolute" && dateRange.length === 2) {
        const [start, end] = dateRange;
        if (start.isAfter(end) || start.isSame(end)) {
          setError("Start date cannot be same or after end date.");
          return;
        }
        fromDate = start.startOf("day").format("YYYY-MM-DD");
        toDate = end.endOf("day").format("YYYY-MM-DD");
      }

      let cities = selectedCities.join(",");

      const statuses = isTutors
        ? Object.values(TUTOR_STATUS)
        : Object.values(STUDENT_STATUS);
      const leadType = isTutors ? LEAD_TYPE.TUTOR : LEAD_TYPE.STUDENT;

      const timestamp = new Date().getTime();
      const url = "/v1/cre/get-new-leads";
      const response = await axiosInstance.get(url, {
        params: {
          leadType,
          isCreatedAt,
          fromDate,
          toDate,
          city: cities,
          _: timestamp,
        },
      });

      const fetchedData = {
        ...response.data.data,
        count: Object.keys(response.data.data.count || {}).reduce(
          (acc, key) => {
            acc[key.toLowerCase()] = response.data.data.count[key];
            return acc;
          },
          {}
        ),
      };

      setSubscribeCount(response.data.data.subscribedCountFromSubsctriptions);
      setExpiryCount(response.data.data.expiredCountFromSubsctriptions);

      // console.log("fetchedData", fetchedData);
      const cards = statuses.map((status) => {
        const currentPath = window.location.href.includes(".com")
          ? window.location.href.split(".com")[0] + ".com"
          : window.location.origin;

        const params = new URLSearchParams();
        isTutors
          ? params.append("filter[tutor_status][0]", status)
          : params.append("filter[status][0]", status);
        params.append("filter[searchFieldType]", "first_name");
        params.append("filter[fromDate]", fromDate);
        params.append("filter[toDate]", toDate);
        params.append("filter[page]", "1");
        params.append("filter[limit]", "10");
        params.append("page", "1");
        params.append("limit", "10");
        params.append("filter[city]", cities);
        params.append("filter[isCreatedAt]", isCreatedAt);
        setLoading(false);
        // console.log("iscreated", isCreatedAt);
        return {
          id: status,
          value: STATUS_DISPLAY_NAME[status?.toUpperCase()] || status,
          details: fetchedData?.count?.[status] || "0",
          icon:
            STATUS_ICONS[
            STATUS_DISPLAY_NAME[status?.toUpperCase()] || status
            ] || STATUS_ICONS["New"],
          link: `${currentPath}/${isTutors ? "tutors" : "students"
            }?${params.toString()}`,
          color: COLOR_OPTIONS[statuses.indexOf(status) % COLOR_OPTIONS.length],
        };
      });
      // console.log("Cards ", cards);
      // console.log(
      //   "Subsceribe count",
      //   subscribeCount,
      //   "Expired count",
      //   expiryCount
      // );
      setFlashcards(cards);
      // console.log("flashcards", flashcards);
      setError(null);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
      setError("Failed to fetch data");
      setFlashcards([]);
    }
  };

  // console.log("flashcards", flashcards);
  const handleToggleChange = (checked) => setIsTutors(checked);
  const handleCreatedAtChange = (checked) => setIsCreatedAt(checked);

  const handleDateChange = (dates) => {
    if (dates) {
      const [start, end] = dates;
      if (start.isAfter(end) || start.isSame(end)) {
        setError("Start date cannot be same or after end date.");
      } else {
        setDateRange(dates);
        setPresetRange("");
        setError(null);
      }
    } else {
      setDateRange([]);
      setPresetRange("");
    }
  };

  const handlePresetRangeChange = (value) => {
    setPresetRange(value);
    setDateRange([]);
  };

  const handleDateRangeTypeChange = (checked) => {
    setDateRangeType(checked ? "absolute" : "relative");
    setDateRange([]);
    setPresetRange("today");
  };

  const handleCityMulriSelect = (value) => {
    if (value.length === 0) {
      return; //Atleast one city should be selected
    }
    setSelectedCities(value);
  };

  return (
    <div className="flash-cards-container">
      <div className="controls-container">
        <div>
          <Switch
            className="toggle"
            checkedChildren="Created At"
            unCheckedChildren="Updated At"
            checked={isCreatedAt}
            onChange={handleCreatedAtChange}
          />
          <Switch
            className="toggle"
            checkedChildren="Tutors"
            unCheckedChildren="Students"
            checked={isTutors}
            onChange={handleToggleChange}
          />
          <Switch
            className="toggle"
            checkedChildren="Absolute"
            unCheckedChildren="Relative"
            checked={dateRangeType === "absolute"}
            onChange={handleDateRangeTypeChange}
          />
        </div>
        <div className="date-controls">
          {dateRangeType === "absolute" ? (
            <RangePicker
              className="date-range-picker"
              value={dateRange}
              onChange={handleDateChange}
              allowClear={false}
              disabledDate={(current) =>
                current && current > dayjs().endOf("day")
              }
            />
          ) : (
            <Select
              className="preset-range-select"
              value={presetRange}
              onChange={handlePresetRangeChange}
            >
              <Option value="today">Today</Option>
              <Option value="yesterday">Yesterday</Option>
              <Option value="this_week">This Week</Option>
              <Option value="last_week">Last Week</Option>
              <Option value="this_month">This Month</Option>
              <Option value="last_month">Last Month</Option>
            </Select>
          )}
        </div>
        <div className="city-controls">
          <span>City:</span>
          <Select
            className="city-select"
            mode="multiple"
            value={selectedCities}
            onChange={handleCityMulriSelect}
            placeholder="Select cities"
            maxTagCount={3}
            maxTagPlaceholder={(omittedValues) =>
              `+${omittedValues.length} selected`
            }
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {isTutors &&
              defaultTutorCities &&
              defaultTutorCities.map((city_id) => (
                <Option key={city_id}>{cities[city_id]}</Option>
              ))}
            {!isTutors &&
              defaultStudentCities &&
              defaultStudentCities.map((city_id) => (
                <Option key={city_id}>{cities[city_id]}</Option>
              ))}
          </Select>
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
      <Row gutter={[16, 16]}>
        {flashcards.slice(0, 14).map((card) => (
          <Col key={card.id}>
            <Card
              loading={loading}
              href={card.link}
              className="card"
              style={{ backgroundColor: card.color }}
              title={
                <Link
                  to={card.link}
                  state={card.state}
                  className="card-title"
                // onClick={() => console.log("Navigating to:", card.link, card.state)}
                >
                  {React.cloneElement(card.icon, { className: "card-icon" })}
                  <span className="card-value">
                    {card.value
                      .replace(/[_]/g, " ")
                      .split(" ")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ")}
                  </span>
                </Link>
              }
              bordered={false}
              extra={
                <Link to={card.link} state={card.state}>
                  <b style={{ color: "white" }}>↑</b>
                </Link>
              }
            >
              <div className="card-content">
                <div className="card-details">
                  <p className="card-details-number">{card.details}</p>
                </div>
              </div>
            </Card>
          </Col>
        ))}

        {/* Divider between sets if needed */}
      </Row>
      {isTutors && (
        <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
          <Col span={24}>
            <Card
              loading={loading}
              title="Subscription Overview"
              bordered={false}
              style={{
                borderRadius: 12,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                backgroundColor: "#80ceed",
                color: "black",
                fontWeight: "bold",
              }}
            >
              <Row gutter={[16, 16]} justify="center">
                <Col
                  xs={24}
                  sm={12}
                  style={{ textAlign: "center", wordWrap: "break-word" }}
                >
                  <h3>Subscription Count</h3>
                  <p
                    style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}
                  >
                    {subscribeCount}
                  </p>
                </Col>
                <Col
                  xs={24}
                  sm={12}
                  style={{
                    textAlign: "center",
                    wordWrap: "break-word",
                  }}
                >
                  <h3>Expired Subscription Count</h3>
                  <p
                    style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}
                  >
                    {expiryCount}
                  </p>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}
      {flashcards.length > 10 && (
        <OverviewCard
          data={flashcards.slice(14)}
          loading={loading}
          subscribeCount={subscribeCount}
          expiryCount={expiryCount}
        />
      )}
    </div>
  );
};

export default FlashCards;
